import { useState } from 'react';
import {
  Autocomplete,
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { InfoOutlined, Close } from '@mui/icons-material';
import { useTheme, alpha, type Theme } from '@mui/material/styles';
import { FieldEvidenceButton } from '../../../components/evidence/FieldEvidenceButton';
import { NumericTextField } from '../../../components/forms/NumericTextField';
import type { DirectShearGeneral } from '../../../types/directShear';
import { directionOfShearOptions, testTypeOptions } from '../config/directShearFields';
import { LeebReadingsInput } from './LeebReadingsInput';

interface CommonParametersFormProps {
  data: DirectShearGeneral;
  onChange: (field: string, value: string | number | null) => void;
  leebTop?: (number | null)[];
  leebBottom?: (number | null)[];
  onLeebChange?: (position: 'Top' | 'Bottom', readings: (number | null)[]) => void;
  readOnly?: boolean;
}

const inputSx = {
  '& .MuiInputBase-input': { py: 0.5, px: 1, fontSize: '0.8rem' },
  '& .MuiInputLabel-root': { fontSize: '0.78rem' },
  '& .MuiInputLabel-shrink': { fontSize: '0.85rem' },
};

const unitAdornment = (unit: string) => (
  <InputAdornment position="end">
    <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.65rem' }}>
      {unit}
    </Typography>
  </InputAdornment>
);

export const CommonParametersForm = ({ data, onChange, leebTop = [], leebBottom = [], onLeebChange = () => {}, readOnly = false }: CommonParametersFormProps) => {
  const theme = useTheme();
  const [jrcOpen, setJrcOpen] = useState(false);

  const handleText = (key: string, raw: string) => {
    onChange(key, raw || null);
  };

  const val = (key: string): string => {
    const v = (data as unknown as Record<string, unknown>)[key];
    return v === null || v === undefined ? '' : String(v);
  };

  // BUG-2026-0210: numeric fields go through the shared NumericTextField
  // (draft-preserving text input). The old `type="number"` + Number(raw)
  // round-trip re-rendered "72." / "72.00" as "72", so the next keystroke
  // produced 720 and inserting a decimal into 7200 dropped the trailing zeros.
  const num = (key: string): number | null => {
    const v = (data as unknown as Record<string, unknown>)[key];
    return typeof v === 'number' ? v : null;
  };

  const numFieldSx = { ...inputSx, width: '100%' };

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2, mb: 1, borderRadius: 2,
        border: `1px solid ${alpha(theme.border.color, 0.35)}`,
        backgroundColor: alpha(theme.palette.background.paper, 0.8),
      }}
    >
      <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1, fontSize: '0.9rem' }}>
        Common Parameters
      </Typography>

      {/* 6-column grid */}
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: 'repeat(6, 1fr)',
        gap: '10px 12px',
      }}>
        {/* Row 1: Geometry — 6 equal fields */}
        <NumericTextField size="small" label="Ellipse A" value={num('ellipseLengthA')}
          onChange={(n) => onChange('ellipseLengthA', n)}
          disabled={readOnly}
          InputProps={{ endAdornment: unitAdornment('mm') }} sx={numFieldSx}
        />
        <NumericTextField size="small" label="Ellipse B" value={num('ellipseLengthB')}
          onChange={(n) => onChange('ellipseLengthB', n)}
          disabled={readOnly}
          InputProps={{ endAdornment: unitAdornment('mm') }} sx={numFieldSx}
        />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <NumericTextField size="small" label="JRC" value={num('jrc')}
            onChange={(n) => onChange('jrc', n)}
            disabled={readOnly}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Tooltip title="View JRC reference chart">
                    <IconButton size="small" onClick={() => setJrcOpen(true)} sx={{ p: 0.25 }}>
                      <InfoOutlined sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              ),
            }}
            sx={{ ...numFieldSx, flex: 1 }}
          />
          <FieldEvidenceButton fieldKey="jrc" />
        </Box>
        <NumericTextField size="small" label="Joint Angle" value={num('jointAngle')}
          onChange={(n) => onChange('jointAngle', n)}
          disabled={readOnly}
          InputProps={{ endAdornment: unitAdornment('\u00B0') }} sx={numFieldSx}
        />
        {/* TASK-017A: Dip and Dip Direction removed — never reported. */}

        {/* Row 2: Config (cols 1-3) + Leeb Hardness (cols 4-6) */}
        <TextField size="small" select label="Test Type" value={val('testType')}
          onChange={(e) => handleText('testType', e.target.value)}
          disabled={readOnly}
          sx={{ ...inputSx, width: '100%' }}
        >
          <MenuItem value="">—</MenuItem>
          {testTypeOptions.map((o) => <MenuItem key={o} value={o}>{o}</MenuItem>)}
        </TextField>
        <Autocomplete
          size="small"
          freeSolo
          disabled={readOnly}
          options={directionOfShearOptions as unknown as string[]}
          value={val('directionOfShear') || ''}
          onChange={(_e, v) => handleText('directionOfShear', (v as string) || '')}
          onInputChange={(_e, v) => handleText('directionOfShear', v || '')}
          renderInput={(params) => (
            <TextField {...params} label="Dir of Shear" sx={{ ...inputSx, width: '100%' }} />
          )}
        />
        {/* BUG-2026-0201: bind to the real state value only. The page seeds the
            common default (DISPLACEMENT_RATE_DEFAULT) into `general` on load,
            so the pre-fill is now a genuine persisted value that reaches the
            report — not a controlled-value fallback that only ever displayed. */}
        <TextField size="small" label="Displacement Rate Description"
          value={val('displacementRateDescription')}
          onChange={(e) => handleText('displacementRateDescription', e.target.value)}
          disabled={readOnly}
          sx={{ ...inputSx, width: '100%', gridColumn: 'span 2' }}
        />
        {/* Sheared Along field removed per user feedback — DB column remains for future use */}
        {/* Row 3: Remarks (left) + Leeb Hardness (right) — same row */}
        <TextField size="small" label="Remarks" value={val('remarks')}
          onChange={(e) => handleText('remarks', e.target.value)}
          placeholder="Optional notes about the sample or test conditions"
          disabled={readOnly}
          multiline rows={3}
          sx={{ ...inputSx, gridColumn: '1 / 4', width: '100%', alignSelf: 'start' }}
        />
        <Box sx={{ gridColumn: '4 / 7' }}>
          <LeebMiniTable onChange={onChange} theme={theme} num={num}
            leebTop={leebTop} leebBottom={leebBottom} onLeebChange={onLeebChange} readOnly={readOnly} />
        </Box>
      </Box>

      {/* JRC Reference Chart — TASK-017E: huge dialog so technicians can compare
          specimen profile against the chart at the largest possible on-screen size.
          Image scrolls inside the dialog so the original aspect/scale is preserved. */}
      <Dialog open={jrcOpen} onClose={() => setJrcOpen(false)} maxWidth={false} fullWidth
        PaperProps={{ sx: { width: '95vw', maxWidth: 1400, height: '92vh' } }}>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
          JRC Reference Chart — Barton &amp; Choubey (1977)
          <IconButton onClick={() => setJrcOpen(false)} size="small"><Close /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ overflow: 'auto' }}>
          <Box component="img" src="/images/jrc-chart.png" alt="JRC Visual Comparison Chart"
            sx={{ display: 'block', mx: 'auto', borderRadius: 1, maxWidth: '100%' }}
            onError={(e: React.SyntheticEvent<HTMLImageElement>) => { e.currentTarget.style.display = 'none'; }}
          />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5, textAlign: 'center' }}>
            Compare joint surface to estimate JRC (0-20). Each profile is 10 cm wide on a properly-calibrated print —
            verify your screen DPI matches if comparing at actual size.
          </Typography>
        </DialogContent>
      </Dialog>
    </Paper>
  );
};

/* ---- Leeb Hardness mini-table component ---- */
interface LeebProps {
  onChange: (field: string, value: string | number | null) => void;
  theme: Theme;
  num: (key: string) => number | null;
  leebTop: (number | null)[];
  leebBottom: (number | null)[];
  onLeebChange: (position: 'Top' | 'Bottom', readings: (number | null)[]) => void;
  readOnly?: boolean;
}

const LeebMiniTable = ({ theme, num, onChange, leebTop, leebBottom, onLeebChange, readOnly = false }: LeebProps) => {
  const cellInput = (key: string) => (
    <NumericTextField
      size="small" value={num(key)}
      onChange={(n) => onChange(key, n)}
      disabled={readOnly}
      sx={{
        width: '100%',
        '& .MuiInputBase-input': {
          py: 0.4, px: 0.5, fontSize: '0.75rem', textAlign: 'center',
        },
        '& .MuiOutlinedInput-notchedOutline': {
          borderColor: alpha(theme.border.color, 0.3),
        },
      }}
    />
  );

  const headerSx = {
    fontSize: '0.65rem', fontWeight: 600, color: 'text.secondary',
    textAlign: 'center' as const, lineHeight: 1.2,
  };
  const rowLabelSx = {
    fontSize: '0.68rem', fontWeight: 500, color: 'text.secondary',
    pr: 0.5, display: 'flex', alignItems: 'center',
  };

  return (
    <Box sx={{
      border: `1px solid ${alpha(theme.border.color, 0.25)}`,
      borderRadius: 1.5, p: 1, pt: 0.5,
      backgroundColor: alpha(theme.palette.background.default, 0.4),
    }}>
      <Typography variant="caption" fontWeight={700} sx={{ fontSize: '0.7rem', mb: 0.5, display: 'block' }}>
        Leeb Hardness
      </Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'auto 1fr 1fr', gap: 0.5, alignItems: 'center' }}>
        <Box />
        <Typography sx={headerSx}>Fresh Surface</Typography>
        <Typography sx={headerSx}>Joint Surface (12 readings)</Typography>
        <Typography sx={rowLabelSx}>Top</Typography>
        {cellInput('leebHardnessFreshSurfaceTop')}
        <LeebReadingsInput position="Top" readings={leebTop} onChange={(r) => onLeebChange('Top', r)} disabled={readOnly} />
        <Typography sx={rowLabelSx}>Bottom</Typography>
        {cellInput('leebHardnessFreshSurfaceBottom')}
        <LeebReadingsInput position="Bottom" readings={leebBottom} onChange={(r) => onLeebChange('Bottom', r)} disabled={readOnly} />
      </Box>
    </Box>
  );
};
